import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, ChevronRight, Calendar } from "lucide-react";
import { useNews } from "@/modules/hub/features/News/hooks/useNews";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { NewsSkeleton } from "./NewsSkeleton";
import { format } from "date-fns";
import {
  trackNewsPageViewed,
  trackNewsCardClick,
} from "@/modules/hub/features/News/analytics/newsAnalytics";

export const News: React.FC = () => {
  const hasTrackedPageView = useRef(false);

  useEffect(() => {
    if (!hasTrackedPageView.current) {
      trackNewsPageViewed();
      hasTrackedPageView.current = true;
    }
  }, []);

  const {
    resources,
    form,
    loading,
    fetchMore,
    sidebarMetadata,
    hasMore,
    handleClearFilters,
    isInitialLoading,
  } = useNews();

  const trades = sidebarMetadata?.trades || [];

  // Handle trade filter
  const selectedTrades = form.watch("trades") || [];

  const toggleTrade = (trade: any) => {
    const currentTrades = form.getValues("trades") || [];
    const isSelected = currentTrades.some((t) => t.id === trade.id);

    if (isSelected) {
      form.setValue(
        "trades",
        currentTrades.filter((t) => t.id !== trade.id),
      );
    } else {
      form.setValue("trades", [...currentTrades, trade]);
    }
  };

  // Only show full skeleton on initial load
  if (isInitialLoading) {
    return <NewsSkeleton />;
  }

  return (
    <MainPageWrapper
      subtitle="Latest updates and insights from across the trades"
      title="News"
    >
      <div className="space-y-6">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-10"
            onChange={(e) => form.setValue("searchTerm", e.target.value)}
            placeholder="Search news..."
            type="search"
            value={form.watch("searchTerm") || ""}
          />
        </div>

        {/* Trade Filters */}
        {trades.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <span className="text-sm font-medium self-center mr-2">
              Filter by trade:
            </span>
            {trades.map((trade: any) => (
              <Button
                key={trade.id}
                onClick={() => toggleTrade(trade)}
                size="sm"
                variant={
                  selectedTrades.some((t: any) => t.id === trade.id)
                    ? "default"
                    : "outline"
                }
              >
                {trade.name}
              </Button>
            ))}
            {selectedTrades.length > 0 && (
              <Button
                onClick={() => form.setValue("trades", [])}
                size="sm"
                variant="ghost"
              >
                Clear filters
              </Button>
            )}
          </div>
        )}

        {/* News Grid */}
        {loading && !isInitialLoading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, index) => (
              <Card className="h-full animate-pulse" key={index}>
                <div className="aspect-video w-full overflow-hidden rounded-t-lg bg-muted" />
                <CardContent className="p-6">
                  <div className="h-3 w-24 bg-muted rounded mb-3" />
                  <div className="h-6 w-full bg-muted rounded mb-2" />
                  <div className="h-6 w-3/4 bg-muted rounded mb-3" />
                  <div className="h-4 w-full bg-muted rounded mb-2" />
                  <div className="h-4 w-full bg-muted rounded mb-2" />
                  <div className="h-4 w-2/3 bg-muted rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : resources.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No news articles found.</p>
          </div>
        ) : (
          <>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {resources.map((article) => (
                <Link
                  className="block hover:no-underline"
                  key={article.id}
                  onClick={() =>
                    trackNewsCardClick({
                      resourceType: "News",
                      resourceUuid: article.uuid,
                    })
                  }
                  to={`/hub/resources/${article.slug}`}
                >
                  <Card className="h-full transition-shadow hover:shadow-lg">
                    {/* Featured Image */}
                    {article.thumbnailUrl && (
                      <div className="aspect-video w-full overflow-hidden rounded-t-lg">
                        <img
                          alt={article.title}
                          className="h-full w-full object-cover"
                          src={article.thumbnailUrl}
                        />
                      </div>
                    )}

                    <CardContent className="p-6">
                      {/* Date */}
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
                        <Calendar className="h-3 w-3" />
                        {article.createdOrPublishStartDate && (
                          <span>
                            {format(
                              new Date(article.createdOrPublishStartDate),
                              "MMM d, yyyy",
                            )}
                          </span>
                        )}
                      </div>

                      {/* Title */}
                      <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                        {article.title}
                      </h3>

                      {/* Tags - removed since not available in search results */}

                      {/* Read More */}
                      <div className="flex items-center gap-1 text-sm font-medium text-primary mt-4">
                        Read more
                        <ChevronRight className="h-4 w-4" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="text-center pt-6">
                <Button
                  disabled={loading}
                  onClick={fetchMore}
                  variant="outline"
                >
                  {loading ? "Loading..." : "Load More News"}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </MainPageWrapper>
  );
};
